import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { inject, Injectable } from "@angular/core";
import { defaultOwnerState, OwnerStateModel } from "./owner-model.interface";
import { Owner } from "./owner.actions";
import { ErrorHandlerService } from "src/app/core/services/firebase-error-handler.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { OwnerService } from "./owner.service";
import { IOwner, UUID } from "src/app/core/interfaces";
import { SecureProState } from "../secure-pro-state/secure-pro.state";
import { UserState } from "../user-state/user.state";


@Injectable()
@State<OwnerStateModel>({
  name: "owners",
  defaults: defaultOwnerState,
})
export class OwnerState {
  private ownersService: OwnerService = inject(OwnerService);
  private snackbar: MatSnackBar = inject(MatSnackBar);
  private errorHandler: ErrorHandlerService = inject(ErrorHandlerService);
  private store: Store = inject(Store);

  @Selector()
  static getIsOwnersLoading(state: OwnerStateModel): OwnerStateModel["isOwnersLoading"] {
    return state.isOwnersLoading;
  }

  @Selector()
  static getIsOwnerLoading(state: OwnerStateModel): OwnerStateModel["isOwnerLoading"] {
    return state.isOwnerLoading;
  }

  @Selector()
  static getIsGovIdUpdating(state: OwnerStateModel) {
    return state.isGovIdUpdating;
  }

  @Selector()
  static getAllOwners(state: OwnerStateModel): OwnerStateModel["owners"] {
    return state.owners;
  }

  @Selector()
  static getOwnerById(state: OwnerStateModel): (ownerId: UUID) => (IOwner | null) {
    return (ownerId: UUID) => state.owners.find(owner => owner.id === ownerId) || null;
  }

  @Selector()
  static getOwnersByEntityId(state: OwnerStateModel): (entityId: UUID) => IOwner[] {
    return (entityId: UUID) => state.owners.filter(owner => owner.entityIds.includes(entityId));
  }


  @Action(Owner.GetAll)
  async getAllOwners({ dispatch, patchState }: StateContext<OwnerStateModel>): Promise<void> {
    try {
      patchState({
        isOwnersLoading: true,
      })

      const owners = await this.ownersService.getAll();

      dispatch(new Owner.GetAllSuccess(owners));
    } catch (e) {
      dispatch(new Owner.GetAllFail(e))
    }
  }

  @Action(Owner.GetAllSuccess)
  getAllOwnersSuccess(
    { patchState }: StateContext<OwnerStateModel>,
    { owners }: Owner.GetAllSuccess
  ): void {
    patchState({
      isOwnersLoading: false,
      owners,
    });
  }

  @Action(Owner.GetAllFail)
  getAllOwnersFail(
    { patchState }: StateContext<OwnerStateModel>,
    { error }: Owner.GetAllFail
  ): void {
    patchState({
      isOwnersLoading: false,
    });

    this.snackbar.open(
      "Something went wrong while loading owners",
      "Dismiss", { duration: 5000 }
    );

    this.errorHandler.handleError(error);
  }

  @Action(Owner.GetById)
  async getOwnerById(
    { dispatch, patchState }: StateContext<OwnerStateModel>,
    { ownerId }: Owner.GetById
  ): Promise<void> {
    try {
      patchState({
        isOwnerLoading: true,
      })

      const owner = await this.ownersService.getById(ownerId);

      dispatch(new Owner.GetByIdSuccess(owner));
    } catch (e) {
      dispatch(new Owner.GetByIdFail(e))
    }
  }

  @Action(Owner.GetByIdSuccess)
  getOwnerByIdSuccess(
    { getState, patchState }: StateContext<OwnerStateModel>,
    { owner }: Owner.GetByIdSuccess
  ): void {
    const owners = [...getState().owners];

    const ownerIndex = owners.findIndex(o => o.id === owner.id);

    if (ownerIndex === -1) {
      owners.push(owner);
    } else {
      owners[ownerIndex] = owner;
    }

    patchState({
      isOwnerLoading: false,
      owners,
    });
  }

  @Action(Owner.GetByIdFail)
  getOwnerByIdFail(
    { patchState }: StateContext<OwnerStateModel>,
    { error }: Owner.GetByIdFail
  ): void {
    patchState({
      isOwnerLoading: false,
    });

    this.snackbar.open(
      "Something went wrong while loading owners",
      "Dismiss", { duration: 5000 }
    );

    this.errorHandler.handleError(error);
  }

  @Action(Owner.GetByEntityId)
  async getOwnersByEntityId(
    { dispatch, patchState }: StateContext<OwnerStateModel>,
    { entityId, orgId }: Owner.GetByEntityId
  ): Promise<void> {
    try {
      patchState({
        isOwnersLoading: true,
      })

      const owners = await this.ownersService.getByEntityId(entityId, orgId);

      dispatch(new Owner.GetByEntityIdSuccess(owners));
    } catch (e) {
      dispatch(new Owner.GetByEntityIdFail(e))
    }
  }

  @Action([Owner.GetByEntityIdSuccess, Owner.SendMultipleAutoCollectEmailsSuccess])
  getOwnersSuccess(
    { getState, patchState }: StateContext<OwnerStateModel>,
    { owners }: Owner.GetByEntityIdSuccess | Owner.SendMultipleAutoCollectEmailsSuccess
  ): void {
    const oldOwners = [...getState().owners];
    const ownersMap = new Map(oldOwners.map(owner => [owner.id, owner]));

    for (const owner of owners) {
      ownersMap.set(owner.id, owner);
    }

    patchState({
      isOwnersLoading: false,
      owners: Array.from(ownersMap.values()),
    });
  }

  @Action(Owner.GetByEntityIdFail)
  getOwnersByEntityIdFail(
    { patchState }: StateContext<OwnerStateModel>,
    { error }: Owner.GetByEntityIdFail
  ): void {
    patchState({
      isOwnersLoading: false,
    });

    this.snackbar.open(
      "Something went wrong while loading owners",
      "Dismiss", { duration: 5000 }
    );

    this.errorHandler.handleError(error);
  }

  @Action(Owner.Create)
  async createOwner(
    { dispatch, patchState }: StateContext<OwnerStateModel>,
    { owner }: Owner.Create
  ): Promise<UUID | null> {
    try {
      patchState({
        isOwnersLoading: true,
      })

      const user = this.store.selectSnapshot(UserState.getCurrentUser);

      const newOwner = await this.ownersService.create(owner, user.id);

      dispatch(new Owner.CreateSuccess(newOwner));

      return newOwner.id;
    } catch (e) {
      dispatch(new Owner.CreateFail(e))
      return null;
    }
  }

  @Action(Owner.CreateSuccess)
  createOwnerSuccess(
    { patchState, getState }: StateContext<OwnerStateModel>,
    { owner }: Owner.CreateSuccess
  ): void {
    const owners = [
      ...getState().owners,
      owner,
    ];
    patchState({
      isOwnersLoading: false,
      owners,
    });
  }

  @Action(Owner.CreateFail)
  createOwnerFail(
    { patchState }: StateContext<OwnerStateModel>,
    { error }: Owner.CreateFail
  ): void {
    patchState({
      isOwnersLoading: false,
    });

    this.snackbar.open(
      "Something went wrong while creating owner",
      "Dismiss", { duration: 5000 }
    );

    this.errorHandler.handleError(error);
  }

  @Action(Owner.Update)
  async updateOwner(
    { dispatch, patchState }: StateContext<OwnerStateModel>,
    { owner }: Owner.Update
  ): Promise<void> {
    try {
      patchState({
        isOwnersLoading: true,
      })

      const user = this.store.selectSnapshot(UserState.getCurrentUser);

      const updatedOwner = await this.ownersService.update(owner, user.id);

      dispatch(new Owner.UpdateSuccess(updatedOwner));
    } catch (e) {
      dispatch(new Owner.UpdateFail(e))
    }
  }

  @Action(Owner.UpdateSuccess)
  updateOwnerSuccess(
    { patchState, getState }: StateContext<OwnerStateModel>,
    { updatedOwner }: Owner.UpdateSuccess
  ): void {
    const owners = getState().owners;

    // In cases where we link an existing owner to an entity,
    // situation may arise where the updated owner was not previously in the application's state.
    // So, we have to check it and handle properly.
    const isUpdatedOwnerInState = owners.some(owner => owner.id === updatedOwner.id);

    const updatedOwners = isUpdatedOwnerInState
      ? owners.map(owner => owner.id === updatedOwner.id ? updatedOwner : owner)
      : [...owners, updatedOwner];

    patchState({
      isOwnersLoading: false,
      owners: updatedOwners,
    });
  }

  @Action(Owner.UpdateFail)
  updateOwnerFail(
    { patchState }: StateContext<OwnerStateModel>,
    { error }: Owner.UpdateFail
  ): void {
    patchState({
      isOwnersLoading: false,
    });

    this.snackbar.open(
      "Something went wrong while updating owner",
      "Dismiss", { duration: 5000 }
    );

    this.errorHandler.handleError(error);
  }

  @Action(Owner.DeleteSuccess)
  deleteOwnerSuccess(
    { patchState, getState }: StateContext<OwnerStateModel>,
    { ownerId }: Owner.DeleteSuccess
  ) {
    const owners = getState().owners.filter(owner => owner.id !== ownerId);

    patchState({
      isOwnersLoading: false,
      owners,
    });
  }

  @Action(Owner.AddEntity)
  async addOwnerEntity(
    { dispatch, patchState }: StateContext<OwnerStateModel>,
    { ownerId, entityId, orgId }: Owner.AddEntity
  ): Promise<void> {
    try {
      patchState({
        isOwnersLoading: true,
      })

      const user = this.store.selectSnapshot(UserState.getCurrentUser);


      const updatedOwner = await this.ownersService.addEntity(ownerId, user.id, entityId, orgId);

      dispatch(new Owner.UpdateSuccess(updatedOwner));
    } catch (e) {
      dispatch(new Owner.UpdateFail(e))
    }
  }

  @Action(Owner.RemoveEntity)
  async removeEntityFromOwner(
    { dispatch, patchState }: StateContext<OwnerStateModel>,
    { ownerId, entityId, orgId }: Owner.RemoveEntity
  ): Promise<void> {
    try {
      patchState({
        isOwnersLoading: true,
      })

      const updatedOwner = await this.ownersService.removeEntity(ownerId, entityId, orgId);

      if (updatedOwner) {
        dispatch(new Owner.UpdateSuccess(updatedOwner));
      } else {
        dispatch(new Owner.DeleteSuccess(ownerId));
      }
    } catch (e) {
      dispatch(new Owner.UpdateFail(e))
    }
  }

  @Action(Owner.RemoveOrgEntities)
  async removeOrgEntitiesFromOwner(
    { dispatch, patchState }: StateContext<OwnerStateModel>,
    { ownerId, orgId }: Owner.RemoveOrgEntities
  ): Promise<void> {
    try {
      patchState({
        isOwnersLoading: true,
      })

      const updatedOwner = await this.ownersService.removeOrgEntities(ownerId, orgId);

      if (updatedOwner) {
        dispatch(new Owner.UpdateSuccess(updatedOwner));
      } else {
        dispatch(new Owner.DeleteSuccess(ownerId));
      }
    } catch (e) {
      dispatch(new Owner.UpdateFail(e))
    }
  }

  @Action(Owner.RemoveEntityFromOwners)
  async removeEntityFromOwners(
    { dispatch, patchState }: StateContext<OwnerStateModel>,
    { ownerIds, entityId, orgId }: Owner.RemoveEntityFromOwners
  ): Promise<void> {
    try {
      patchState({
        isOwnersLoading: true,
      })

      const updatedOwnersMap = await this.ownersService.removeEntityFromOwners(ownerIds, entityId, orgId);

      dispatch(new Owner.RemoveEntityFromOwnersSuccess(updatedOwnersMap));
    } catch (e) {
      dispatch(new Owner.UpdateFail(e))
    }
  }

  @Action(Owner.RemoveEntityFromOwnersSuccess)
  removeEntityFromOwnersSuccess(
    { getState, patchState }: StateContext<OwnerStateModel>,
    { updatedOwnersMap }: Owner.RemoveEntityFromOwnersSuccess
  ): void {
    const owners = [...getState().owners];

    const ownersMap = new Map(owners.map(owner => [owner.id, owner]));

    for (const [ownerId, updatedOwner] of Object.entries(updatedOwnersMap)) {
      if (updatedOwner) {
        ownersMap.set(ownerId, updatedOwner);
      } else {
        ownersMap.delete(ownerId);
      }
    }
    const updatedOwners = Array.from(ownersMap.values());

    patchState({
      isOwnersLoading: false,
      owners: updatedOwners,
    });
  }

  @Action(Owner.RemoveEntityFromOwnersFail)
  removeEntityFromOwnersFail(
    { patchState }: StateContext<OwnerStateModel>,
    { error }: Owner.RemoveEntityFromOwnersFail
  ): void {
    patchState({
      isOwnersLoading: false,
    });

    this.snackbar.open(
      "Something went wrong while removing entity from owners",
      "Dismiss", { duration: 5000 }
    );

    this.errorHandler.handleError(error);
  }

  @Action(Owner.UploadGovIdImage)
  async uploadGovIdImage(
    { patchState, dispatch }: StateContext<OwnerStateModel>,
    { ownerId, image, mediaType }: Owner.UploadGovIdImage
  ): Promise<void> {
    try {
      patchState({
        isGovIdUpdating: true,
      });

      const updatedOwner = await this.ownersService.uploadGovIdImageByOwnerId(image, mediaType, ownerId);

      dispatch(new Owner.ManageGovIdImageSuccess(updatedOwner));
    } catch (e) {
      dispatch(new Owner.ManageGovIdImageFail(e));
    }
  }

  @Action(Owner.ReplaceGovIdImage)
  async replaceGovIdImage(
    { patchState, dispatch }: StateContext<OwnerStateModel>,
    { ownerId, image, mediaType, docUrl }: Owner.ReplaceGovIdImage
  ): Promise<void> {
    try {
      patchState({
        isGovIdUpdating: true,
      });

      const updatedOwner = await this.ownersService.replaceGovId(image, mediaType, ownerId, docUrl);

      dispatch(new Owner.ManageGovIdImageSuccess(updatedOwner));
    } catch (e) {
      dispatch(new Owner.ManageGovIdImageFail(e));
    }
  }

  @Action(Owner.DeleteGovIdImage)
  async deleteGovIdImage(
    { patchState, dispatch }: StateContext<OwnerStateModel>,
    { ownerId, docUrl }: Owner.DeleteGovIdImage
  ): Promise<void> {
    try {
      patchState({
        isGovIdUpdating: true,
      });

      const updatedOwner = await this.ownersService.deleteGovId(ownerId, docUrl);

      dispatch(new Owner.ManageGovIdImageSuccess(updatedOwner));
    } catch (e) {
      dispatch(new Owner.ManageGovIdImageFail(e));
    }
  }

  @Action(Owner.ManageGovIdImageSuccess)
  manageGovIdImageSuccess(
    { patchState, getState }: StateContext<OwnerStateModel>,
    { updatedOwner }: Owner.ManageGovIdImageSuccess
  ): void {
    const owners = getState().owners.map(
      owner => owner.id === updatedOwner.id ? updatedOwner : owner
    );

    patchState({
      isGovIdUpdating: false,
      owners,
    });
  }

  @Action(Owner.ManageGovIdImageFail)
  manageGovIdImageFail(
    { patchState }: StateContext<OwnerStateModel>,
    { error }: Owner.ManageGovIdImageFail
  ): void {
    patchState({
      isGovIdUpdating: false,
    });

    this.snackbar.open(
      "Something went wrong while managing owner's GovId image",
      "Dismiss", { duration: 5000 }
    );

    this.errorHandler.handleError(error);
  }

  @Action(Owner.SendAutoCollectEmail)
  async sendAutoCollectEmail(
    { dispatch, patchState }: StateContext<OwnerStateModel>,
    { owner, entityId, legalName }: Owner.SendAutoCollectEmail
  ): Promise<void> {
    try {
      patchState({
        isOwnersLoading: true,
      });

      const user = this.store.selectSnapshot(UserState.getCurrentUser);

      let updatedOwner: IOwner;

      switch (user.userType) {
        case "secure-pro": {
          const org = this.store.selectSnapshot(SecureProState.getOrg);

          updatedOwner = await this.ownersService.proSendSingleAutoCollectEmail(
            owner,
            org.name,
            org.id,
            org.customEmailContent || undefined,
          )

          break;
        }
        case "secure-file": {
          if (!entityId) {
            throw new Error();
          }
          if (!legalName) {
            throw new Error();
          }

          updatedOwner = await this.ownersService.fileSendSingleAutoCollectEmail(
            owner,
            legalName,
            entityId,
          );
          break;
        }
        default:
          throw new Error("Unexpected user type");
      }

      dispatch(new Owner.UpdateSuccess(updatedOwner));
    } catch (e) {
      dispatch(new Owner.UpdateFail(e))
    }
  }

  @Action(Owner.SendMultipleAutoCollectEmails)
  async sendMultipleAutoCollectEmails(
    { dispatch, patchState }: StateContext<OwnerStateModel>,
    { ownerIds, orgId, entityName }: Owner.SendMultipleAutoCollectEmails
  ): Promise<void> {
    try {
      patchState({
        isOwnersLoading: true,
      })

      const updatedOwners = await this.ownersService.sendMultipleAutoCollectEmails(
        ownerIds,
        orgId,
        entityName
      );

      dispatch(new Owner.SendMultipleAutoCollectEmailsSuccess(updatedOwners));
    } catch (e) {
      dispatch(new Owner.SendMultipleAutoCollectEmailsFail(e))
    }
  }
}

